import React, { useContext } from 'react';
import { Player } from '@lottiefiles/react-lottie-player';
import styled from '@emotion/styled';
import { Theme, ThemeContext } from '../../utilities/theme';
import SPACING, { SpacingKeys } from '../size';
import { ColorCodes, FUTURO as C } from '../color';

export type SpinnerColor = 'orange' | 'grey' | 'white';

export type SpinnerProps = {
  size: SpacingKeys | string;
  full?: boolean;
  color?: SpinnerColor;
};

const getColor = (theme: Theme, color: SpinnerColor) => {
  let colorScheme: {
    [color in SpinnerColor]: { spinner: ColorCodes; circle: ColorCodes };
  };
  switch (theme) {
    case 'futuro-dark':
      colorScheme = {
        orange: { spinner: C.DARK.SWEET_ORANGE, circle: C.DARK.PEACH },
        grey: { spinner: C.DARK.SWEET_ORANGE, circle: C.DARK.SHADOW },
        white: { spinner: C.DARK.WHITE, circle: C.DARK.PEACH },
      };
      break;
    case 'futuro':
    default:
      colorScheme = {
        orange: { spinner: C.LIGHT.SWEET_ORANGE, circle: C.LIGHT.PEACH },
        grey: { spinner: C.LIGHT.SWEET_ORANGE, circle: C.LIGHT.PEACH },
        white: { spinner: C.LIGHT.WHITE, circle: C.LIGHT.PEACH },
      };
  }
  return colorScheme[color];
};

const Wrapper = styled('div')<{
  spinner: string;
  circle: string;
  full: boolean;
}>`
  .spinner * {
    stroke: ${p => p.spinner};
  }
  .circle * {
    stroke: ${p => p.circle};
  }
  display: ${p => (p.full ? 'block' : 'inline-block')};
`;

const FILE = `${process.env.IMAGE_URL}/lottie/spinner.lottie`;

/**
 * A futuro spinner
 *
 * @argument size string - either a size as defined in the spacing system, or a
 *     raw css size value.
 * @argument color string - "orange", "white" or "steel", the color options for
 *     the spinner
 * @argument full boolean - display block if true and inline-block if false
 */
const Spinner = ({ size, color = 'orange', full = true }: SpinnerProps) => {
  const width = SPACING[size] || size;
  const theme = useContext(ThemeContext);

  const colors = getColor(theme, color);

  return (
    <Wrapper full={full} {...colors} role="status" aria-label="Loading">
      <Player src={FILE} style={{ width }} loop autoplay />
    </Wrapper>
  );
};

/**
 * Center the spinner in whatever wrapping container it exists in
 *
 * @example
 *
 * <SomeConstantSizedWrapperDiv>
 *   <SpinnerCenterHelper>
 *     <Spinner size="XXL" />
 *   </SpinnerCenterHelper>
 * </SomeConstantSizedWrapperDiv>
 */
export const SpinnerCenterHelper = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
  height: 100%;
`;

export default Spinner;
